import { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { TLearnerPlanListItemDto } from "@/api/services/main/learner/dtos/get-learner-plan-list.dto";
import { Table, TableSorting } from "@/components/elements/Table";
import { useTable } from "@/hooks/useTable";
import { EMPTY_STR } from "@/constants/string";
import { YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { dayjs } from "@/utils/dayjs";
import { divider } from "@/theme/colors";
import { formatLearningHours, formatMockExamScore, formatProgressPercent } from "@/features/learner/utils/formatLearnerPlan";

type TLearnerPlanListTableProps = {
  rows: TLearnerPlanListItemDto[];
  fullName: string;
  industryName: string;
  isLoading: boolean;
};

export const LearnerPlanListTable: FC<TLearnerPlanListTableProps> = ({ rows, fullName, industryName, isLoading }) => {
  const columns = useMemo<MRT_ColumnDef<TLearnerPlanListItemDto>[]>(
    () => [
      {
        id: "fullName",
        header: "氏名",
        size: 146,
        Cell: () => fullName || EMPTY_STR.TEXT,
        Header: <TableSorting name="learner.fullName">氏名</TableSorting>,
      },
      {
        id: "industryName",
        header: "受験分野",
        size: 120,
        Cell: () => industryName || EMPTY_STR.TEXT,
        Header: <TableSorting name="industry.name">受験分野</TableSorting>,
      },
      {
        id: "examPeriodName",
        header: "受験予定",
        size: 132,
        Cell: ({ row }) => row.original.examPeriodName ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="plan.examPeriodName">受験予定</TableSorting>,
      },
      {
        id: "examScheduledYearMonth",
        header: "予定年月",
        size: 88,
        Cell: ({ row }) => {
          const value = row.original.examScheduledYearMonth;
          return value && dayjs(value).isValid() ? dayjs(value).format(YEAR_MONTH_FORMAT) : EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="plan.examScheduledYearMonth">予定年月</TableSorting>,
      },
      {
        id: "learningSeconds",
        header: "学習時間",
        size: 120,
        Cell: ({ row }) => formatLearningHours(row.original.learningSeconds),
        Header: <TableSorting name="plan.learningSeconds">学習時間</TableSorting>,
      },
      {
        id: "progressPercent",
        header: "学習進捗",
        size: 120,
        Cell: ({ row }) => formatProgressPercent(row.original.progressPercent),
        Header: <TableSorting name="plan.progressPercent">学習進捗</TableSorting>,
      },
      {
        id: "mockExamScore",
        header: "直近模試結果",
        size: 120,
        Cell: ({ row }) => formatMockExamScore(row.original.mockExamScore),
        Header: <TableSorting name="plan.mockExamScore">直近模試結果</TableSorting>,
      },
    ],
    [fullName, industryName],
  );

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => row.id.toString(),
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    renderEmptyRowsFallback: () => <Box />,
  });

  return <Table table={table} isLoading={isLoading} headRowHeight={48} borderColor={divider.high} />;
};
